import {
  ORDER_SERIES_BUCKETS,
  countConditions,
  storeDate,
  type IsoDate,
  type OrderSeries,
  type OrderSeriesBucket,
  type OrderSeriesMetric,
  type OrderSeriesSplit,
} from "@estelle/domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { ForbiddenError } from "@/adapters/api/base";
import { useDataSource } from "@/adapters/data-source";
import {
  EmptyState,
  LoadFailed,
  NoAccess,
  NoMatches,
} from "@/components/shell/EmptyState";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import { MultiLineChart, type LineSeries } from "@/components/charts/lazy";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney } from "@/modules/gst/format";
import { DateRangeControls } from "@/modules/reports/DateRangeControls";
import { ReportHeader } from "@/modules/reports/ReportHeader";
import { Segmented } from "@/modules/reports/Segmented";
import {
  RANGE_PRESETS,
  SPLIT_CHOICES,
  bucketDetail,
  bucketLabel,
  decodeChart,
  effectiveRange,
  encodeChart,
  lineColours,
  lineLabel,
  presetRange,
  rangeLabel,
  type ChartState,
} from "@/modules/reports/orderChart";
import type { SectionScreenProps } from "@/modules/types";
import { formatCompactMoney, formatCount } from "@/lib/chart-format";
import { cn } from "@/lib/utils";
import { reportingNow } from "@/lib/reportClock";
import { useSectionSearch, type SectionSearch } from "@/routes/search";

const BUCKET_LABELS: Record<OrderSeriesBucket, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
};

const METRIC_LABELS: { value: OrderSeriesMetric; label: string }[] = [
  { value: "orders", label: "Orders" },
  { value: "value", label: "Order value" },
];

const NO_SPLIT = "none";

/**
 * Orders over time, as a line chart (#449).
 *
 * The chart is the answer to one aggregate query, `orders.series`, which the
 * server counts in the store's timezone — no order is fetched to be counted
 * here. What narrows it is the shared filter builder in the toolbar above,
 * the same one the Orders list uses, so every order field works: payment,
 * status, tags, state, source, a product. The settings the chart owns itself —
 * dates, day/week/month, orders or value, the split, whether cancelled orders
 * count — sit in one row above it and live in the URL beside the filter, so a
 * view can be linked to and saved.
 */
export function Orders({
  allFields,
  filter,
  search,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const navigate = useNavigate();
  const urlSearch = useSectionSearch();

  const state = useMemo(() => decodeChart(urlSearch.chart), [urlSearch.chart]);
  const today = storeDate(reportingNow());
  const range = effectiveRange(state, today);

  function change(patch: Partial<ChartState>) {
    const next = encodeChart({ ...state, ...patch });
    void navigate({
      to: ".",
      search: (prev: SectionSearch) => ({ ...prev, chart: next }),
    });
  }

  const presets = useMemo(
    () =>
      RANGE_PRESETS.map((preset) => ({
        ...preset,
        ...presetRange(preset.id, today),
      })),
    [today],
  );

  const request = {
    filter,
    search,
    from: range.from,
    to: range.to,
    bucket: state.bucket,
    metric: state.metric,
    ...(state.splitBy ? { splitBy: state.splitBy } : {}),
    includeCancelled: state.includeCancelled,
  };

  const query = useQuery({
    queryKey: ["reports", "orders-series", request],
    queryFn: () => data.orders.series(request),
    // Keep the last chart on screen, dimmed, while the next one is counted: a
    // skeleton on every control change makes the page jump.
    placeholderData: keepPreviousData,
  });

  const series: OrderSeries | undefined = query.data;
  const filtered = countConditions(filter) > 0 || Boolean(search);

  const splitField = series?.splitBy
    ? allFields.find((field) => field.name === series.splitBy)
    : undefined;

  const lines = useMemo<LineSeries[]>(() => {
    if (!series) return [];
    const money = series.metric === "value";
    const note = (total: string) =>
      money ? formatMoney(total) : formatCount(Number(total));

    if (series.splitBy === null) {
      const only = series.lines[0];
      return [
        {
          id: "all",
          label: money ? "Order value" : "Orders",
          values: (only?.values ?? []).map(Number),
          color: "var(--primary)",
          note: only ? note(only.total) : undefined,
        },
      ];
    }

    const options = splitField?.options;
    const ranked = series.lines.filter((line) => !line.other);
    const colours = lineColours(
      ranked.map((line) => line.key ?? "\u0000none"),
      options?.map((option) => option.value) ?? [],
    );

    return series.lines.map((line) => ({
      id: line.other ? "\u0000other" : (line.key ?? "\u0000none"),
      label: line.other
        ? "Other"
        : lineLabel(line.key, options, series.splitBy),
      values: line.values.map(Number),
      color: line.other
        ? "var(--muted-foreground)"
        : (colours[ranked.indexOf(line)] ?? "var(--series-1)"),
      note: note(line.total),
    }));
  }, [series, splitField]);

  // What the chart on screen is, not what the controls now say: while the next
  // one is being counted the last one stays up, dimmed, and its heading and
  // figures have to keep describing it.
  const money = (series?.metric ?? state.metric) === "value";
  const shownBucket = series?.bucket ?? state.bucket;
  const formatValue = (value: number) =>
    money ? formatMoney(value.toFixed(2)) : formatCount(value);

  const rangeText = rangeLabel(
    series?.from ?? range.from,
    series?.to ?? range.to,
  );
  const totalText = series
    ? money
      ? formatMoney(series.totals.value)
      : `${formatCount(series.totals.orders)} ${series.totals.orders === 1 ? "order" : "orders"}`
    : "";

  // The section is hidden from someone without both permissions, but a typed
  // URL, or a role changed since the page loaded, still lands here: say so, in
  // the shared words, instead of printing a failed request.
  if (query.error instanceof ForbiddenError) {
    return (
      <NoAccess permission={query.error.permission ?? "orders.list.view"} />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ReportHeader
        readAt={series ? query.dataUpdatedAt : undefined}
        controls={
          <section aria-label="Report dates">
            <DateRangeControls
              idPrefix="orders-chart"
              range={range}
              today={today}
              presets={presets}
              onChange={(next) => {
                change({ range: next });
              }}
              align="end"
            />
          </section>
        }
      />
      {/*
        One row of settings above the chart, not inside it: they scope
        everything below, and the Filters above them are the other half of the
        same scope.
      */}
      <section aria-label="Chart settings" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Segmented
            label="Group by"
            value={state.bucket}
            options={ORDER_SERIES_BUCKETS.map((bucket) => ({
              value: bucket,
              label: BUCKET_LABELS[bucket],
            }))}
            onChange={(bucket) => {
              change({ bucket });
            }}
          />
          <Segmented
            label="Measure"
            value={state.metric}
            options={METRIC_LABELS}
            onChange={(metric) => {
              change({ metric });
            }}
          />

          <div className="flex items-center gap-2">
            <Label htmlFor="orders-chart-split" className="text-xs">
              Split by
            </Label>
            <Select
              value={state.splitBy ?? NO_SPLIT}
              onValueChange={(next) => {
                change({
                  splitBy:
                    next === NO_SPLIT ? null : (next as OrderSeriesSplit),
                });
              }}
            >
              <SelectTrigger id="orders-chart-split" size="sm" className="w-60">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SPLIT}>Nothing — one line</SelectItem>
                {SPLIT_CHOICES.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="orders-chart-cancelled"
              checked={state.includeCancelled}
              onCheckedChange={(checked) => {
                change({ includeCancelled: checked === true });
              }}
            />
            <Label
              htmlFor="orders-chart-cancelled"
              className="text-sm font-normal"
            >
              Include cancelled orders
            </Label>
          </div>
        </div>
      </section>

      <ChartCard
        title={`${money ? "Order value" : "Orders"} over time`}
        description={
          <>
            {series ? `${totalText} · ` : ""}
            {rangeText}, by {shownBucket}
            {series?.splitBy ? `, split by ${splitNoun(series.splitBy)}` : ""}.
            {shownBucket === "week"
              ? " Weeks run Monday to Sunday; only days inside the dates count."
              : shownBucket === "month"
                ? " Only days inside the dates count in the first and last month."
                : ""}
            {/*
              What the figures are, said where they are read. "Order value" is
              the order's total as it was placed — refunds are not taken off
              it — and a cancelled order is in the chart only when the switch
              above says so.
            */}
            <span data-testid="chart-basis" className="mt-1 block">
              {money
                ? "Order value is each order's total as placed; refunds are not deducted. "
                : ""}
              {state.includeCancelled
                ? "Cancelled orders are included."
                : "Cancelled orders are left out; turn on “Include cancelled orders” to count them."}
            </span>
          </>
        }
        table={
          series && series.totals.orders > 0 ? (
            <FigureTable
              rows={series.buckets}
              rowKey={(date) => date}
              columns={[
                {
                  header: BUCKET_LABELS[series.bucket],
                  cell: (date) => bucketDetail(date, series.bucket),
                },
                ...lines.map((line) => ({
                  header: line.label,
                  numeric: true,
                  cell: (_: IsoDate, index: number) =>
                    formatValue(line.values[index] ?? 0),
                })),
              ]}
            />
          ) : undefined
        }
      >
        {query.isError ? (
          <LoadFailed
            message={query.error.message}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : !series ? (
          <Skeleton className="h-64 w-full" />
        ) : series.totals.orders === 0 ? (
          filtered ? (
            <NoMatches
              onClear={onClearFilter}
              description={`No orders match the filter between ${rangeText}.`}
            />
          ) : (
            <EmptyState
              title="No orders in these dates"
              description={`No orders were placed between ${rangeText}.${state.includeCancelled ? "" : " Cancelled orders are left out."}`}
            />
          )
        ) : (
          <div
            aria-busy={query.isPlaceholderData}
            className={cn(
              "transition-opacity",
              query.isPlaceholderData && "opacity-50",
            )}
          >
            <MultiLineChart
              labels={series.buckets.map((date) =>
                bucketLabel(date, series.bucket),
              )}
              detailLabels={series.buckets.map((date) =>
                bucketDetail(date, series.bucket),
              )}
              series={lines}
              ariaLabel={`${money ? "Order value" : "Orders"} per ${series.bucket}, ${rangeText}. Total ${totalText}.`}
              formatValue={formatValue}
              formatTick={money ? formatCompactMoney : formatCount}
            />
          </div>
        )}
      </ChartCard>
    </div>
  );
}

function splitNoun(split: OrderSeriesSplit): string {
  return SPLIT_CHOICES.find((choice) => choice.value === split)?.noun ?? split;
}
